const libPictView = require('pict-view');

// Cap the rendered set list: a heavily-referenced material can transitively reach a lot of specification
// sets, and dumping them all through one template pass is both slow and stack-risky. Show the first CAP
// with an honest "showing X of Y" note.
const RENDER_CAP = 250;
// Meadow filter list length guard — chunk the INN lookups so the FilteredTo URL never gets huge.
const INN_CHUNK = 150;

/**
 * MaterialSpecSetsTab — a read-only "Test Specification Sets" tab for a Material's read view.
 *
 * This is the reciprocal of SpecSetMaterialsTab. A material is not linked to a SET directly; it links to
 * individual Test Specifications (TestSpecificationMaterialJoin), and each spec belongs to a set
 * (TestSpecification.IDTestSpecificationSet). So "the sets this material belongs to" is a walk back up:
 * material → the specs joined to it → the distinct sets those specs live in. This view resolves the current
 * material id the same way ChildListTab does — from AppData.MaterialDetails, the record the read view stashes
 * — then renders a compact, scrollable table. Wired in as a { Type: 'View' } read tab (see MasterList-Builder
 * / Headlight-MasterLists ViewTabs on Material).
 */
class MaterialSpecSetsTab extends libPictView
{
	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get destination()
	{
		return this.options.DefaultDestinationAddress || `#PSRS-Tab-TestSpecificationSets`;
	}

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => (pError ? reject(pError) : resolve(pBody)));
		});
	}

	_materialID()
	{
		const tmpMaterial = this.pict.AppData.MaterialDetails || {};
		return tmpMaterial.IDMaterial;
	}

	_escapeHTML(pValue)
	{
		return String((pValue === null || typeof pValue === 'undefined') ? '' : pValue)
			.replace(/[&<>"]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[pChar]);
	}

	_chunk(pArray, pSize)
	{
		const tmpChunks = [];
		for (let i = 0; i < pArray.length; i += pSize) { tmpChunks.push(pArray.slice(i, i + pSize)); }
		return tmpChunks;
	}

	/**
	 * Resolve the distinct specification sets this material's specs belong to. Returns { sets, total }.
	 * Each hydrated set carries a SpecCount — how many of THIS material's test specs live in that set.
	 */
	async _loadSpecSets(pMaterialID)
	{
		// Hop 1: the material's Test Specification join rows -> the specs it is attached to.
		const tmpJoins = await this._getJSON(`TestSpecificationMaterialJoins/FilteredTo/FBV~IDMaterial~EQ~${ pMaterialID }/0/5000`);
		const tmpSpecIDSet = {};
		(Array.isArray(tmpJoins) ? tmpJoins : []).forEach((pJoin) => { if (pJoin.IDTestSpecification > 0) { tmpSpecIDSet[pJoin.IDTestSpecification] = true; } });
		const tmpSpecIDs = Object.keys(tmpSpecIDSet);
		if (tmpSpecIDs.length < 1) { return { sets: [], total: 0 }; }

		// Hop 2: those specs -> their sets (chunked INN), de-duplicated to distinct set ids. Along the way,
		// tally how many of the material's specs fall in each set so the tab can show that context.
		const tmpSetSpecCount = {};
		for (const tmpChunk of this._chunk(tmpSpecIDs, INN_CHUNK))
		{
			const tmpSpecs = await this._getJSON(`TestSpecifications/FilteredTo/FBL~IDTestSpecification~INN~${ tmpChunk.join(',') }/0/${ INN_CHUNK }`);
			(Array.isArray(tmpSpecs) ? tmpSpecs : []).forEach((pSpec) =>
			{
				if (pSpec.IDTestSpecificationSet > 0) { tmpSetSpecCount[pSpec.IDTestSpecificationSet] = (tmpSetSpecCount[pSpec.IDTestSpecificationSet] || 0) + 1; }
			});
		}
		const tmpSetIDs = Object.keys(tmpSetSpecCount);
		const tmpTotal = tmpSetIDs.length;
		if (tmpTotal < 1) { return { sets: [], total: 0 }; }

		// Hop 3: hydrate only the first RENDER_CAP sets for display, attaching the per-set spec tally.
		const tmpDisplayIDs = tmpSetIDs.slice(0, RENDER_CAP);
		const tmpSets = [];
		for (const tmpChunk of this._chunk(tmpDisplayIDs, INN_CHUNK))
		{
			const tmpRows = await this._getJSON(`TestSpecificationSets/FilteredTo/FBL~IDTestSpecificationSet~INN~${ tmpChunk.join(',') }~FSF~Name~ASC~0/0/${ INN_CHUNK }`);
			(Array.isArray(tmpRows) ? tmpRows : []).forEach((pRow) => tmpSets.push(Object.assign({ SpecCount: tmpSetSpecCount[pRow.IDTestSpecificationSet] || 0 }, pRow)));
		}
		// Per-chunk sorting is only local; sort the assembled list by name so multi-chunk results read in order.
		tmpSets.sort((pA, pB) => String(pA.Name || '').localeCompare(String(pB.Name || '')));
		return { sets: tmpSets, total: tmpTotal };
	}

	/**
	 * The read view's `View` tab type calls renderAsync() with no arguments. Load the sets the material's
	 * specs belong to, stash the render model, and paint. Guarded so a failed hop shows a notice, not a blank.
	 */
	async renderAsync()
	{
		const tmpState = this.pict.AppData.MaterialSpecSets = this.pict.AppData.MaterialSpecSets || {};
		const tmpMaterialID = this._materialID();
		if (!tmpMaterialID)
		{
			this.pict.ContentAssignment.assignContent(this.destination, `<div class="mss-note">Select a material to see its specification sets.</div>`);
			return;
		}
		this.pict.ContentAssignment.assignContent(this.destination, `<div class="mss-note">Loading specification sets…</div>`);
		try
		{
			const { sets, total } = await this._loadSpecSets(tmpMaterialID);
			tmpState.Rows = sets.map((pSet) => (
				{
					Name: this._escapeHTML(pSet.Name || '—'),
					Year: this._escapeHTML(pSet.Year || '—'),
					SpecCount: this._escapeHTML(pSet.SpecCount || 0),
					GUID: this._escapeHTML(pSet.GUIDTestSpecificationSet || ''),
				}));
			tmpState.Count = total;
			tmpState.Shown = sets.length;
			tmpState.TruncatedSlot = (total > sets.length) ? [ { Shown: sets.length, Total: total } ] : [];
			tmpState.RowsSlot = (sets.length > 0) ? [ tmpState ] : [];
			tmpState.EmptySlot = (sets.length < 1) ? [ {} ] : [];
			// Render into the destination the read view assigned this tab (not a fixed renderable address,
			// which would only match one tab hash) — parse the container template against AppData.
			const tmpHTML = this.pict.parseTemplateByHash('MaterialSpecSets-Container-Template', {});
			this.pict.ContentAssignment.assignContent(this.destination, tmpHTML);
			this.pict.CSSMap.injectCSS();
		}
		catch (pError)
		{
			this.pict.log.warn(`MaterialSpecSetsTab: load failed for material ${ tmpMaterialID }: ${ pError && (pError.message || pError) }`);
			this.pict.ContentAssignment.assignContent(this.destination, `<div class="mss-note">Could not load the specification sets for this material.</div>`);
		}
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

const _DEFAULT_CONFIGURATION =
{
	ViewIdentifier: 'MaterialSpecSets',
	DefaultDestinationAddress: '#PSRS-Tab-TestSpecificationSets',
	DefaultRenderable: 'MaterialSpecSets-Container',

	CSS: /*css*/`
.mss-wrap { padding: 0.25rem 0; }
.mss-head { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--theme-color-brand-primary, #5650e6); margin-bottom: 0.5rem; }
.mss-count { display: inline-block; margin-left: 0.4rem; padding: 0.02rem 0.5rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700; background: var(--theme-color-background-tertiary, #eef1f6); color: var(--theme-color-text-secondary, #45505f); }
.mss-note { font-size: 0.9rem; color: var(--theme-color-text-muted, #6b7686); padding: 0.5rem 0; }
.mss-trunc { font-size: 0.8rem; color: var(--theme-color-text-muted, #6b7686); padding: 0.35rem 0 0; }
.mss-scroll { overflow-x: auto; }
.mss-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.mss-table th { position: sticky; top: 0; background: var(--theme-color-background-primary, #fff); text-align: left; font-weight: 600; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #6b7686); padding: 0.4rem 0.7rem; border-bottom: 1px solid var(--theme-color-border-default, #d7dce3); white-space: nowrap; }
.mss-table td { padding: 0.45rem 0.7rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f6); color: var(--theme-color-text-primary, #1f2733); }
.mss-table td.mss-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.mss-table tbody tr:hover { background: var(--theme-color-background-tertiary, #f4f6f9); }
`,

	Templates:
	[
		{
			Hash: 'MaterialSpecSets-Container-Template',
			Template: /*html*/`
				<div class="mss-wrap">
					<div class="mss-head">Test Specification Sets <span class="mss-count">{~D:AppData.MaterialSpecSets.Count~}</span></div>
					{~TS:MaterialSpecSets-Empty:AppData.MaterialSpecSets.EmptySlot~}
					{~TS:MaterialSpecSets-Table:AppData.MaterialSpecSets.RowsSlot~}
					{~TS:MaterialSpecSets-Trunc:AppData.MaterialSpecSets.TruncatedSlot~}
				</div>`
		},
		{
			Hash: 'MaterialSpecSets-Table',
			Template: /*html*/`<div class="mss-scroll"><table class="mss-table"><thead><tr><th>Specification Set</th><th>Year</th><th>Specs</th></tr></thead><tbody>{~TS:MaterialSpecSets-Row:Record.Rows~}</tbody></table></div>`
		},
		{
			Hash: 'MaterialSpecSets-Row',
			Template: /*html*/`<tr><td>{~D:Record.Name~}</td><td>{~D:Record.Year~}</td><td class="mss-num">{~D:Record.SpecCount~}</td></tr>`
		},
		{
			Hash: 'MaterialSpecSets-Empty',
			Template: /*html*/`<div class="mss-note">This material is not referenced by any test specification sets.</div>`
		},
		{
			Hash: 'MaterialSpecSets-Trunc',
			Template: /*html*/`<div class="mss-trunc">Showing the first {~D:Record.Shown~} of {~D:Record.Total~} specification sets.</div>`
		},
	],

	Renderables:
	[
		{
			RenderableHash: 'MaterialSpecSets-Container',
			TemplateHash: 'MaterialSpecSets-Container-Template',
			DestinationAddress: '#PSRS-Tab-TestSpecificationSets',
			RenderMethod: 'replace',
		},
	],
};

module.exports = MaterialSpecSetsTab;
module.exports.default_configuration = _DEFAULT_CONFIGURATION;
